import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { PageShell, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { ShippingFeeSection } from '@/features/shippingFee/components/ShippingFeeSection';
import { OverseasShippingSection } from '@/features/overseasShipping/components/OverseasShippingSection';

/** 탭 식별자 — ?tab= deep-link 키. 'domestic'=국내 배송비, 'overseas'=해외 배송비. */
const TAB_DOMESTIC = 'domestic';
const TAB_OVERSEAS = 'overseas';
const TAB_VALUES = [TAB_DOMESTIC, TAB_OVERSEAS] as const;
type ShippingTab = (typeof TAB_VALUES)[number];

export const normalizeShippingTab = (raw: string | null): ShippingTab =>
  (TAB_VALUES as ReadonlyArray<string>).includes(raw ?? '') ? (raw as ShippingTab) : TAB_DOMESTIC;

/**
 * 탭 전환 시 다음 쿼리스트링을 만든다 — 기존 쿼리는 보존하고 tab 만 교체한다.
 * (happy-dom 은 Radix 트리거 click 으로 onValueChange 를 신뢰성 있게 발화하지 못하므로,
 *  URL 갱신 로직은 순수 함수로 분리해 단위 검증한다.)
 */
export const buildTabSearchParams = (prev: URLSearchParams, tab: string): URLSearchParams => {
  const next = new URLSearchParams(prev);
  next.set('tab', tab);
  return next;
};

/**
 * 국내 배송비조건 + 해외 배송비 설정을 한 페이지의 탭으로 통합한 컨테이너.
 *
 * 각 탭은 기존 단독 페이지의 본문(ShippingFeeSection / OverseasShippingSection)을 그대로
 * 재사용하며 로직/훅/API 는 무변경이다. 활성 탭은 ?tab= 쿼리로 deep-link 가능하다.
 */
export const ShippingManagementPage = () => {
  const { t } = useTranslation('shipping');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = normalizeShippingTab(searchParams.get('tab'));

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams((prev) => buildTabSearchParams(prev, value), { replace: true });
    },
    [setSearchParams],
  );

  return (
    <PageShell
      title={t('shipping.title')}
      breadcrumb={[
        { label: t('shipping.breadcrumb.products'), href: '/products' },
        { label: t('shipping.breadcrumb.shipping') },
      ]}
      description={t('shipping.description')}
      data-testid="shipping-management-page"
    >
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList aria-label={t('shipping.tabs.ariaLabel')}>
          <TabsTrigger value={TAB_DOMESTIC} data-testid="shipping-tab-domestic">
            {t('shipping.tabs.domestic')}
          </TabsTrigger>
          <TabsTrigger value={TAB_OVERSEAS} data-testid="shipping-tab-overseas">
            {t('shipping.tabs.overseas')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={TAB_DOMESTIC}>
          <ShippingFeeSection />
        </TabsContent>
        <TabsContent value={TAB_OVERSEAS}>
          <OverseasShippingSection />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
};
